import React from 'react'
import type {
  FeatureFlagRow,
  FeatureFlagSocietyRow,
  FeatureFlagUserRow,
} from '../../../generated/api/features'
import { formatDate } from '../../../helpers/date'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useModal } from '../../../hooks/useModal'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../common/Button'
import { Card } from '../../common/Card'
import { CardBody } from '../../common/Card/CardBody'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { Toggle } from '../../common/Toggle'
import { AddFeatureFlagSociety } from './AddFeatureFlagSociety'
import { AddFeatureFlagUser } from './AddFeatureFlagUser'
import { useSwitchFeatureFlag } from './useSwitchFeatureFlag'

export const FeatureFlagCard = ({
  flag,
}: {
  flag: FeatureFlagRow
}): JSX.Element => {
  const { translate } = useAppTranslation()
  const { mutate, isPending } = useSwitchFeatureFlag()
  const { show, open, close } = useModal()
  const { feature } = flag
  const addedLine = ({
    addedByName,
    addedAt,
  }: Pick<
    FeatureFlagUserRow | FeatureFlagSocietyRow,
    'addedByName' | 'addedAt'
  >): string => {
    const date = formatDate(addedAt)
    return addedByName == null
      ? translate('adminView.featureFlags.added', { date })
      : translate('adminView.featureFlags.addedBy', { name: addedByName, date })
  }
  const confirmation = flag.global
    ? {
        title: translate('adminView.featureFlags.confirmGlobalOff.title', {
          feature,
        }),
        description: translate(
          'adminView.featureFlags.confirmGlobalOff.description'
        ),
      }
    : {
        title: translate('adminView.featureFlags.confirmGlobalOn.title', {
          feature,
        }),
        description: translate(
          'adminView.featureFlags.confirmGlobalOn.description'
        ),
      }

  return (
    <>
      <Card className="w-full max-w-[720px]">
        {{
          body: (
            <CardBody className="flex flex-col gap-4 text-left">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p style={theme.textVariants.baseBold}>{feature}</p>
                  <p style={theme.textVariants.base}>{flag.description}</p>
                </div>
                <Toggle
                  name={`feature-flag-${feature}-global`}
                  label={translate('adminView.featureFlags.global')}
                  enabled={flag.global}
                  disabled={isPending}
                  onChange={open}
                />
              </div>

              <p style={theme.textVariants.baseBold}>
                {translate('adminView.featureFlags.users')}
              </p>
              {flag.users.length === 0 ? (
                <p style={theme.textVariants.base}>
                  {translate('adminView.featureFlags.noUsers')}
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {flag.users.map((user) => (
                    <li
                      key={user.userId}
                      className="flex items-center justify-between gap-4"
                    >
                      <div>
                        <p style={theme.textVariants.baseBold}>{user.name}</p>
                        <p style={theme.textVariants.caption}>{user.email}</p>
                        <p style={theme.textVariants.caption}>
                          {addedLine(user)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={ButtonVariant.DANGER}
                        label={translate('adminView.featureFlags.remove')}
                        disabled={isPending}
                        onClick={() =>
                          mutate({ feature, userId: user.userId, on: false })
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}

              <AddFeatureFlagUser
                userIdsWithFlag={flag.users.map((user) => user.userId)}
                disabled={isPending}
                onAdd={(userId) => mutate({ feature, userId, on: true })}
              />

              <p style={theme.textVariants.baseBold}>
                {translate('adminView.featureFlags.societies')}
              </p>
              {flag.societies.length === 0 ? (
                <p style={theme.textVariants.base}>
                  {translate('adminView.featureFlags.noSocieties')}
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {flag.societies.map((society) => (
                    <li
                      key={society.societyId}
                      className="flex items-center justify-between gap-4"
                    >
                      <div>
                        <p style={theme.textVariants.baseBold}>
                          {society.name}
                        </p>
                        {(society.city ?? '').length > 0 && (
                          <p style={theme.textVariants.caption}>
                            {society.city}
                          </p>
                        )}
                        <p style={theme.textVariants.caption}>
                          {addedLine(society)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={ButtonVariant.DANGER}
                        label={translate('adminView.featureFlags.remove')}
                        disabled={isPending}
                        onClick={() =>
                          mutate({
                            feature,
                            societyId: society.societyId,
                            on: false,
                          })
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}

              <AddFeatureFlagSociety
                societyIdsWithFlag={flag.societies.map(
                  (society) => society.societyId
                )}
                disabled={isPending}
                onAdd={(societyId) => mutate({ feature, societyId, on: true })}
              />
            </CardBody>
          ),
        }}
      </Card>

      {show && (
        <ConfirmationModal
          show={show}
          close={close}
          deleteMode={false}
          title={confirmation.title}
          description={confirmation.description}
          onConfirm={() => mutate({ feature, on: !flag.global })}
        />
      )}
    </>
  )
}
